import { useRouterState } from '@tanstack/react-router';

import { useSubsidiaryDashboardConfiguration } from '~/features/configuration/hooks/useSubsidiaryDashboardConfiguration';
import type { ConfigurationPlatform } from '~/features/configuration/types/configuration.types';

import { ExperimentPlatform } from '../types/experiment.types';

export type PerformanceViewMode = 'default' | 'category';

/** Dashboard key that gates the category-level performance sub-tab. */
const CATEGORY_DASHBOARD_KEY = 'category_performance';

/** Maps ExperimentPlatform to the ConfigurationPlatform expected by the dashboards API.
 *  Null and unknown platforms fall back to GMC; the result is ignored when platform is null. */
function toConfigPlatform(platform: ExperimentPlatform | null): ConfigurationPlatform {
  if (platform === ExperimentPlatform.META) return 'META';
  return 'GMC';
}

/**
 * Resolves which Performance sub-tab variant to render for the current experiment.
 *
 * Resolution order:
 *   1. URL override `?performanceView=default|category` (dev / QA escape hatch).
 *   2. Dashboard Configuration API — `category_performance` enabled for the experiment's platform.
 *   3. Default `'default'`.
 *
 * All hooks are called unconditionally; logic is applied after.
 */
export function usePerformanceViewMode(platform: ExperimentPlatform | null): PerformanceViewMode {
  const search = useRouterState({ select: (state) => state.location.search });
  // Always call the dashboard hook — useSubsidiaryDashboardConfiguration gates
  // itself on the resolved platform UUID so no X-Platform-ID header leaks when
  // it's missing. Reads the active subsidiary's resolved (override-or-default)
  // dashboard list rather than the global platform-wide catalog, since this
  // page is viewed by subsidiary users, not just superadmins on the system tenant.
  const { data: dashboards } = useSubsidiaryDashboardConfiguration(toConfigPlatform(platform));

  const override = readOverride(search);
  if (override !== undefined) return override;

  // Only consider the API result when we know the experiment's platform; null
  // means the backend hasn't exposed a platform yet, so stay on the safe default.
  if (platform !== null) {
    const enabled = dashboards?.find((d) => d.key === CATEGORY_DASHBOARD_KEY)?.enabled ?? false;
    if (enabled) return 'category';
  }

  return 'default';
}

/** Pulls the `?performanceView` override from the URL; returns `undefined` if absent or invalid. */
function readOverride(search: unknown): PerformanceViewMode | undefined {
  if (!search || typeof search !== 'object') return undefined;
  const value = (search as Record<string, unknown>).performanceView;
  if (value === 'default' || value === 'category') return value;
  return undefined;
}
